import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom'; 
import api from '../api/axios';
import Swal from 'sweetalert2';
import { X, Copy, Archive, Trash2, Send, ArrowUpDown, ChevronUp, ChevronDown, CalendarPlus } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';


const TrendBadge = ({ value }) => {
  const numValue = parseFloat(value) || 0;
  let badgeContent;
  
  if (numValue > 0) {
    badgeContent = <span className="text-[12px] font-medium tracking-wide text-emerald-600 dark:text-emerald-400">+{numValue}% ↗</span>;
  } else if (numValue < 0) {
    badgeContent = <span className="text-[12px] font-medium tracking-wide text-rose-600 dark:text-rose-400">{Math.abs(numValue)}% ↘</span>;
  } else {
    badgeContent = <span className="text-[12px] font-medium tracking-wide text-slate-500 dark:text-slate-500">0% –</span>;
  }

  return (
    <div className="relative group cursor-help flex items-center">
      {badgeContent}
      
      {/* Floating Tooltip */}
      <div className="absolute bottom-full right-0 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-50">
        <div className="bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-[12px] font-bold tracking-wide px-3 py-1.5 rounded-lg shadow-xl whitespace-nowrap relative">
          vs previous 30 days
          {/* Tooltip Arrow Pointer */}
          <div className="absolute top-full right-4 -mt-px border-4 border-transparent border-t-slate-900 dark:border-t-white"></div>
        </div>
      </div>
    </div>
  );
};

const CHART_COLORS = { "Selected": "#10B981", "On Hold": "#F59E0B", "Rejected": "#EF4444", "Pending": "#6366F1" };

const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 15; 
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  if (percent === 0) return null;
  return (
    <text x={x} y={y} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" className="text-[12px] font-bold tracking-[0.1em] fill-slate-700 dark:fill-white">
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const getRowColor = (decision) => {
  switch(decision) {
    case 'selected': return 'bg-emerald-50/80 hover:bg-emerald-100 dark:bg-emerald-500/5 dark:hover:bg-emerald-500/10';
    case 'rejected': return 'bg-rose-50/80 hover:bg-rose-100 dark:bg-rose-500/5 dark:hover:bg-rose-500/10';
    case 'on_hold': return 'bg-amber-50/80 hover:bg-amber-100 dark:bg-amber-500/5 dark:hover:bg-amber-500/10';
    default: return 'hover:bg-white dark:hover:bg-white/5'; 
  }
};

const Dashboard = () => {
  const navigate = useNavigate(); 

  const [stats, setStats] = useState({ 
    active_pipelines: 0, active_trend: 0, interviews_scheduled: 0, scheduled_trend: 0,
    pending_decisions: 0, pending_trend: 0, interviews_completed: 0, completed_trend: 0,
    decision_breakdown: { "Selected": 0, "Rejected": 0, "On Hold": 0, "Pending": 0 }
  });
  
  const [activeCampaigns, setActiveCampaigns] = useState([]);
  const [completedCampaigns, setCompletedCampaigns] = useState([]); 

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignDetails, setCampaignDetails] = useState([]);
  
  const [newEmail, setNewEmail] = useState('');
  const [isAdding, setIsAdding] = useState(false); 
  
  const [draftDecisions, setDraftDecisions] = useState({});
  const [decisionSort, setDecisionSort] = useState('default');

  const fetchDashboardData = async () => {
    try {
      const statsRes = await api.get('/dashboard-stats');
      setStats(statsRes.data);
      const activeRes = await api.get('/dashboard/active-campaigns');
      setActiveCampaigns(activeRes.data);
      const completedRes = await api.get('/dashboard/completed-campaigns');
      setCompletedCampaigns(completedRes.data);
    } catch (error) { console.error("Failed to fetch dashboard data", error); } 
  };

  useEffect(() => { 
    fetchDashboardData(); 

    // Listen for global header refresh
    const handleGlobalRefresh = () => fetchDashboardData();
    window.addEventListener('app-refresh', handleGlobalRefresh);
    return () => window.removeEventListener('app-refresh', handleGlobalRefresh);
  }, []);

  const pieData = Object.keys(stats.decision_breakdown).map(key => ({
    name: key, value: stats.decision_breakdown[key]
  })).filter(item => item.value > 0); 

  const fetchDetails = async (campaignId) => {
    try { 
      const res = await api.get(`/campaigns/${campaignId}/details`); 
      
      // 🔥 FRONTEND MATH FALLBACK: Calculate expirations dynamically inside the roster
      const normalizedDetails = res.data.map(detail => {
          let backendStatus = detail.status ? String(detail.status).toLowerCase().trim() : 'pending';
          
          // Defensive check: use whichever date field your backend provides for the candidate
          let dateField = detail.created_at || detail.date || detail.invited_at; 
          
          if (dateField && backendStatus === 'pending') {
              const timeString = dateField.endsWith('Z') ? dateField : `${dateField}Z`;
              const createdAtDate = new Date(timeString);
              const now = new Date();
              const diffInHours = Math.abs(now - createdAtDate) / (1000 * 60 * 60);
              
              if (diffInHours >= 24) {
                  backendStatus = 'expired';
              }
          }
          return { ...detail, status: backendStatus };
      });
      
      setCampaignDetails(normalizedDetails); 
    } 
    catch (error) { console.error("Failed to fetch details"); } 
  };

  const handleOpenDrawer = (campaign) => { 
    setSelectedCampaign(campaign); setIsDrawerOpen(true); setDecisionSort('default'); setDraftDecisions({}); fetchDetails(campaign.id); 
  };
  
  const closeDrawer = () => { 
    setIsDrawerOpen(false); setTimeout(() => { setSelectedCampaign(null); setCampaignDetails([]); setNewEmail(''); setDraftDecisions({}); }, 300); 
  };

  const handleAddCandidate = async () => {
  if (!newEmail) return;
  setIsAdding(true);
  try {
    await api.post(`/campaigns/${selectedCampaign.id}/add_candidate`, { email: newEmail });
    setNewEmail('');
    await fetchDetails(selectedCampaign.id);
  } catch (e) {
    const detail = e.response?.data?.detail;
    if (detail?.code === 'QUOTA_EXCEEDED') {
      Swal.fire({
        icon: 'warning',
        title: 'Plan limit reached',
        text: detail.message,
        confirmButtonColor: '#4F46E5',
      });
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Failed to add candidate.',
        confirmButtonColor: '#4F46E5',
      });
    }
  } finally { setIsAdding(false); }
};

  const handleRemoveCandidate = async (candidateId) => {
    if (!window.confirm("Are you sure you want to remove this candidate?")) return;
    try { await api.delete(`/campaigns/${selectedCampaign.id}/candidates/${candidateId}`); fetchDetails(selectedCampaign.id); } 
    catch (error) { Swal.fire({ icon: 'error', title: 'Failed to remove candidate.', confirmButtonColor: '#4F46E5' }); }
  };

  const handleMarkCompleted = async (e, campaignId) => {
    e.stopPropagation(); 
    if (!window.confirm("Move this campaign to Archived?")) return;
    try { await api.post(`/campaigns/${campaignId}/complete`); fetchDashboardData(); } catch (error) { Swal.fire({ icon: 'error', title: 'Failed to update status', confirmButtonColor: '#4F46E5' }); }
  };

  const handleBulkSave = async () => {
    const candidateIds = Object.keys(draftDecisions);
    if (candidateIds.length === 0) return;
    try {
      await Promise.all(candidateIds.map(id => api.put(`/campaigns/${selectedCampaign.id}/candidates/${id}/decision`, { decision: draftDecisions[id] })));
      setDraftDecisions({}); fetchDetails(selectedCampaign.id); fetchDashboardData(); 
    } catch (error) { Swal.fire({ icon: 'error', title: 'Failed to save some decisions.', confirmButtonColor: '#4F46E5' }); }
  };

  const isEditable = (campaign) => campaign && campaign.status && campaign.status.toLowerCase() !== 'completed';

  const sortedCampaignDetails = useMemo(() => {
    if (decisionSort === 'default') return campaignDetails;
    const rank = { selected: 1, on_hold: 2, pending: 3, rejected: 4 };
    return [...campaignDetails].sort((a, b) => {
      const rankA = rank[draftDecisions[a.candidate_id] || a.recruiter_decision || 'pending'];
      const rankB = rank[draftDecisions[b.candidate_id] || b.recruiter_decision || 'pending'];
      return decisionSort === 'asc' ? rankA - rankB : rankB - rankA;
    });
  }, [campaignDetails, decisionSort, draftDecisions]);


  return (
    <div className="w-full max-w-[1600px] mx-auto px-6 lg:px-12 py-12 lg:py-20 transition-colors duration-500">
      
      {/* --- MINIMALIST KPI GRID --- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-12 mb-16 lg:mb-24">
        <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-px bg-slate-200 dark:bg-white/10 border border-slate-200 dark:border-white/10 rounded-[2rem] overflow-hidden">
          {[
            { label: "Active Campaigns", val: stats.active_pipelines, trend: stats.active_trend },
            { label: "Interviews Scheduled", val: stats.interviews_scheduled, trend: stats.scheduled_trend },
            { label: "Pending Decisions", val: stats.pending_decisions, trend: stats.pending_trend },
            { label: "Interviews Completed", val: stats.interviews_completed, trend: stats.completed_trend }
          ].map((kpi, i) => (
            <div key={i} className="bg-white dark:bg-[#0a0a0a] p-8 flex flex-col justify-between hover:bg-slate-50 dark:hover:bg-[#111111] transition-colors">
              <span className="text-sm font-semibold tracking-wide text-slate-500 dark:text-slate-400 mb-8">{kpi.label}</span>
              <div className="flex items-end justify-between">
                <span className="text-5xl font-light tracking-tighter text-slate-900 dark:text-white">{kpi.val}</span>
                <TrendBadge value={kpi.trend} />
              </div>
            </div>
          ))}
        </div>

        {/* Minimal Donut Chart */}
        <div className="bg-white dark:bg-[#0a0a0a] p-8 border border-slate-200 dark:border-white/10 rounded-[2rem] flex flex-col">
           <h3 className="text-sm font-semibold tracking-wide text-slate-500 dark:text-slate-400 mb-8">Recruitment Funnel</h3>
           <div className="flex-1 min-h-[200px] relative">
             {pieData.length === 0 ? (
                <div className="absolute inset-0 flex items-center justify-center text-sm tracking-wide text-slate-400">No Data</div>
             ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={75} paddingAngle={2} dataKey="value" stroke="none" label={renderCustomizedLabel} labelLine={false}>
                      {pieData.map((entry, index) => <Cell key={`cell-${index}`} fill={CHART_COLORS[entry.name]} />)}
                    </Pie>
                    <RechartsTooltip contentStyle={{ borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: '#1e293b', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3)' }} itemStyle={{ fontWeight: '600', letterSpacing: '0.1em', color: '#f8fafc', textTransform: 'uppercase', fontSize: '10px' }} cursor={{fill: 'transparent'}} />
                  </PieChart>
                </ResponsiveContainer>
             )}
           </div>
           <div className="flex flex-wrap justify-center gap-4 mt-6">
             {Object.keys(CHART_COLORS).map(key => (
               <div key={key} className="flex items-center gap-2">
                 <span className="w-2 h-2 rounded-full" style={{ backgroundColor: CHART_COLORS[key] }}></span>
                 <span className="text-[13px] tracking-wide text-slate-600 dark:text-slate-400">{key}</span>
               </div>
             ))}
           </div>
        </div>
      </div>

      {/* --- ACTIVE CAMPAIGNS --- */}
      <div className="mb-16 lg:mb-24">
        <h3 className="text-2xl font-medium tracking-wide text-slate-900 dark:text-white mb-8 border-b border-slate-200 dark:border-white/10 pb-4">Active Campaigns</h3>
        <div className="overflow-x-auto pb-4">
          <table className="w-full text-left min-w-[800px]">
            <thead>
              <tr>
                <th className="px-4 py-4 font-semibold text-slate-500 dark:text-slate-400 text-[13px] tracking-wide">Campaign Name</th>
                <th className="px-4 py-4 font-semibold text-slate-500 dark:text-slate-400 text-[13px] tracking-wide">Format</th>
                <th className="px-4 py-4 font-semibold text-slate-500 dark:text-slate-400 text-[13px] tracking-wide">Pipeline</th>
                <th className="px-4 py-4 font-semibold text-slate-500 dark:text-slate-400 text-[13px] tracking-wide">Status</th>
                <th className="px-4 py-4 font-semibold text-slate-500 dark:text-slate-400 text-[13px] tracking-wide text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-white/5 border-t border-slate-200 dark:border-white/5">
              {activeCampaigns.map((camp) => {
                const compRate = camp.invited_count > 0 ? (camp.completed_count / camp.invited_count) * 100 : 0;
                return (
                <tr key={camp.id} onClick={() => handleOpenDrawer(camp)} className="group cursor-pointer hover:bg-white dark:hover:bg-white/5 transition-colors">
                  <td className="px-4 py-6">
                    <div className="text-base font-medium tracking-wide text-slate-900 dark:text-white mb-1 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">{camp.title}</div>
                    <div className="text-[12px] text-slate-500 dark:text-slate-400 tracking-wide">{new Date(camp.created_at).toLocaleDateString()}</div>
                  </td>
                  <td className="px-4 py-6 text-xs text-slate-600 dark:text-slate-400 tracking-wide">
                    {camp.is_custom ? 'Custom Workflow' : 'AI Agent'}
                  </td>
                  <td className="px-4 py-6">
                    <div className="flex items-center gap-4">
                      <div className="text-[12px] tracking-wide text-slate-600 dark:text-slate-500 w-24">{camp.completed_count} / {camp.invited_count} Done</div>
                      <div className="w-32 h-1 bg-slate-200 dark:bg-white/10 rounded-none overflow-hidden">
                        <div className="h-full bg-indigo-600 dark:bg-white transition-all duration-1000" style={{ width: `${compRate}%` }}></div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-6">
                    <div className="flex items-center gap-2 text-[12px] tracking-wide text-emerald-600 dark:text-emerald-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> Active
                    </div>
                  </td>
                  <td className="px-4 py-6 text-right">
                    <div className="flex items-center justify-end gap-6">
                      <span className="text-[12px] tracking-wide text-slate-500 dark:text-slate-400 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">View</span>
                      <button onClick={(e) => handleMarkCompleted(e, camp.id)} className="text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 transition-colors" title="Archive">
                        <Archive size={16} strokeWidth={1.5} />
                      </button>
                    </div>
                  </td>
                </tr>
              )})}
              
              {/* --- NEW EMPTY STATE FOR DASHBOARD --- */}
              {activeCampaigns.length === 0 && (
                <tr>
                  <td colSpan="5" className="py-20 text-center">
                    <div className="flex flex-col items-center justify-center animate-in fade-in duration-500">
                      <div className="w-16 h-16 bg-slate-50 dark:bg-white/5 rounded-full flex items-center justify-center mb-5 border border-slate-100 dark:border-white/10 shadow-sm dark:shadow-none">
                        <CalendarPlus size={24} className="text-slate-400 dark:text-slate-500" strokeWidth={1.5} />
                      </div>
                      <h3 className="text-lg font-medium tracking-wide text-slate-900 dark:text-white mb-2">No Active Campaigns</h3>
                      <p className="text-slate-500 dark:text-slate-400 text-xs tracking-wide mb-6">Launch a new campaign to start interviewing.</p>
                      
                      <button 
                        onClick={() => navigate('/schedule')} 
                        className="bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-500 text-white px-8 py-3 rounded-xl text-xs font-bold tracking-wide shadow-md dark:shadow-[0_0_15px_rgba(99,102,241,0.3)] transition-all active:scale-95"
                      >
                        Schedule Campaign
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- ARCHIVED CAMPAIGNS --- */}
      {completedCampaigns.length > 0 && (
        <div className="mb-16">
          <h3 className="text-2xl font-medium tracking-wide text-slate-900 dark:text-white mb-8 border-b border-slate-200 dark:border-white/10 pb-4">Archived Campaigns</h3>
          <div className="overflow-x-auto pb-4 opacity-80 hover:opacity-100 transition-opacity duration-500">
            <table className="w-full text-left min-w-[800px]">
              <thead>
                <tr>
                  <th className="px-4 py-4 font-semibold text-slate-500 dark:text-slate-400 text-[13px] tracking-wide">Campaign Name</th>
                  <th className="px-4 py-4 font-semibold text-slate-500 dark:text-slate-400 text-[13px] tracking-wide">Final Progress</th>
                  <th className="px-4 py-4 font-semibold text-slate-500 dark:text-slate-400 text-[13px] tracking-wide">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-white/5 border-t border-slate-200 dark:border-white/5">
                {completedCampaigns.map((camp) => (
                  <tr key={camp.id} onClick={() => handleOpenDrawer(camp)} className="cursor-pointer hover:bg-white dark:hover:bg-white/5 transition-colors">
                    <td className="px-4 py-6"><div className="text-base font-medium tracking-wide text-slate-900 dark:text-white">{camp.title}</div></td>
                    <td className="px-4 py-6 text-xs text-slate-600 dark:text-slate-400 tracking-wide">{camp.completed_count} / {camp.invited_count} Completed</td>
                    <td className="px-4 py-6"><span className="text-[12px] tracking-wide text-slate-500 dark:text-slate-400">Archived</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* --- SLIDE-OVER DRAWER --- */}
      <div className={`fixed inset-0 bg-white/80 dark:bg-black/80 backdrop-blur-sm z-50 transition-opacity duration-500 ${isDrawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={closeDrawer}></div>

      <div className={`fixed inset-y-0 right-0 w-full md:w-3/4 max-w-4xl bg-slate-50 dark:bg-[#111] border-l border-slate-200 dark:border-white/10 z-50 flex flex-col transform transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${isDrawerOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        {selectedCampaign && (
          <>
            <div className="px-8 py-8 md:px-12 md:py-12 flex justify-between items-start border-b border-slate-200 dark:border-white/10">
              <div>
                <h2 className="text-3xl font-semibold text-slate-900 dark:text-white tracking-wide mb-2">{selectedCampaign.title}</h2>
                <p className="text-[12px] text-slate-500 dark:text-slate-400 tracking-wide">Candidate Roster</p>
              </div>
              <button onClick={closeDrawer} className="text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"><X size={28} strokeWidth={1}/></button>
            </div>

            {isEditable(selectedCampaign) && (
              <div className="px-8 py-6 md:px-12 md:py-8 border-b border-slate-200 dark:border-white/10 bg-white dark:bg-[#0a0a0a]">
                <div className="flex flex-col sm:flex-row gap-4">
                  <input 
                      value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="Enter candidate email..." 
                      className="flex-1 bg-transparent border-b border-slate-300 dark:border-white/20 text-slate-900 dark:text-white px-2 py-3 text-sm font-light focus:border-indigo-600 dark:focus:border-white outline-none transition-colors rounded-none placeholder-slate-400"
                  />
                  <button 
                      onClick={handleAddCandidate} disabled={isAdding || !newEmail}
                      className="bg-slate-900 dark:bg-white text-white dark:text-black px-8 py-3 text-xs font-medium tracking-wide hover:bg-slate-800 dark:hover:bg-slate-200 transition-colors disabled:opacity-30 flex items-center justify-center gap-2"
                  >
                      <Send size={14} strokeWidth={1.5}/> Invite
                  </button>
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto overflow-x-auto px-8 md:px-12 py-8">
              <table className="w-full text-left min-w-[700px]">
                <thead className="text-[13px] tracking-wide text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-white/10">
                  <tr>
                    <th className="pb-4 pl-4 font-semibold">Candidate</th>
                    <th className="pb-4 font-semibold">Status</th>
                    <th className="pb-4 font-semibold cursor-pointer hover:text-slate-900 dark:hover:text-white transition-colors flex items-center gap-2" onClick={() => setDecisionSort(decisionSort === 'asc' ? 'desc' : 'asc')}>
                      Decision {decisionSort !== 'default' && (decisionSort === 'asc' ? <ChevronUp size={12}/> : <ChevronDown size={12}/>)}
                    </th>
                    <th className="pb-4 pr-4 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                  {sortedCampaignDetails.map((detail, idx) => {
                    const currentDecision = draftDecisions[detail.candidate_id] || detail.recruiter_decision || 'pending';
                    return (
                      <tr key={idx} className={`group transition-colors ${getRowColor(currentDecision)}`}>
                        <td className="py-6 pl-4 pr-4 text-sm font-light text-slate-900 dark:text-white tracking-wide">{detail.email}</td>
                        <td className="py-6 pr-4">
                          <span className={`text-[12px] tracking-wide ${
                            detail.status === 'completed' ? 'text-emerald-600 dark:text-emerald-400' : 
                            detail.status === 'expired' ? 'text-rose-500 dark:text-rose-400' : 
                            'text-slate-500'
                          }`}>
                            {detail.status}
                          </span>
                        </td>
                        <td className="py-6 pr-4">
                           {detail.status === 'completed' ? (
                              <select 
                                value={currentDecision} onChange={(e) => setDraftDecisions({ ...draftDecisions, [detail.candidate_id]: e.target.value })}
                                className="bg-transparent text-xs font-semibold tracking-wide border-b border-slate-300 dark:border-white/20 text-slate-900 dark:text-white py-1 outline-none cursor-pointer focus:border-indigo-600 dark:focus:border-white transition-colors"
                              >
                                <option value="pending" className="bg-white dark:bg-[#111]">Pending</option>
                                <option value="selected" className="bg-white dark:bg-[#111] text-emerald-600 dark:text-emerald-400">Selected</option>
                                <option value="on_hold" className="bg-white dark:bg-[#111] text-amber-600 dark:text-amber-400">On Hold</option>
                                <option value="rejected" className="bg-white dark:bg-[#111] text-rose-600 dark:text-rose-400">Rejected</option>
                              </select>
                           ) : detail.status === 'expired' ? (
                              <span className="text-[12px] tracking-wide text-rose-400 dark:text-rose-600 font-medium">Expired</span>
                           ) : (
                              <span className="text-[12px] tracking-wide text-slate-400 dark:text-slate-600">Awaiting</span>
                           )}
                        </td>
                        <td className="py-6 pr-4 text-right flex items-center justify-end gap-6 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => {navigator.clipboard.writeText(detail.session_link); Swal.fire({ icon: 'success', title: 'Copied!', confirmButtonColor: '#4F46E5' });}} className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"><Copy size={16} strokeWidth={1.5} /></button>
                          {isEditable(selectedCampaign) && <button onClick={() => handleRemoveCandidate(detail.candidate_id)} className="text-slate-500 dark:text-slate-400 hover:text-rose-500 transition-colors"><Trash2 size={16} strokeWidth={1.5} /></button>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {Object.keys(draftDecisions).length > 0 && (
              <div className="px-8 py-6 md:px-12 bg-white dark:bg-[#0a0a0a] border-t border-slate-200 dark:border-white/10 flex justify-between items-center animate-in slide-in-from-bottom-2 fade-in">
                <span className="text-xs tracking-wide text-indigo-600 dark:text-indigo-400 font-medium">Unsaved Edits</span>
                <div className="flex gap-4">
                  <button onClick={() => setDraftDecisions({})} className="text-xs tracking-wide text-slate-500 hover:text-slate-900 dark:hover:text-white transition-colors">Discard</button>
                  <button onClick={handleBulkSave} className="text-xs tracking-wide font-semibold text-slate-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Commit Changes</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;